import React, { useCallback, useState } from "react";
import ProductConfigurationModal from "../ProductConfigurationModal";
import { FormattedMessage } from "react-intl";

const isDev = process.env.NODE_ENV === "development";

/**
 * @callback AddToRequisitionListFunction
 * @param {string | null} [requisitionListId] if falsy will create a new requisition list (through a user popup) then add items to it
 * @param {import('./withAllOptionsSet').NormalizedItems} [items] if falsy add all items
 */

/**
 * @typedef {object} InputProps
 * @property {import('./EnhanceAddToRequisitionList').OutputProps["addToRequisitionList"]} addToRequisitionList
 * @property {import('./EnhanceAddToRequisitionList').OutputProps["items"]} items
 * @property {import('./EnhanceAddToRequisitionList').OutputProps["areAllOptionsSet"]} areAllOptionsSet
 * @property {import('./EnhanceAddToRequisitionList').OutputProps["showOptionsModalIfNotFullyConfigured"]} showOptionsModalIfNotFullyConfigured
 */

/**
 * @typedef {object} OutputProps
 * @property {AddToRequisitionListFunction} addToRequisitionList
 * @property {JSX.Element} productConfigurationModal
 */

/**
 * @param {React.ComponentType<BaseProps & InputProps & OutputProps>} BaseComponent
 * @returns {React.FunctionComponent<BaseProps & InputProps>}
 * @template BaseProps
 */
const withProductConfigurationModal = (BaseComponent) => (props) => {
  const {
    addToRequisitionList,
    items,
    areAllOptionsSet,
    showOptionsModalIfNotFullyConfigured,
  } = props;

  const [isConfigurationPickerModalOpen, setIsConfigurationPickerModalOpen] =
    useState(false);
  const [addToRequisitionListArgs, setAddToRequisitionListArgs] = useState({
    requisitionListId: null,
    items: [],
  });

  const addToRequisitionListOverride = useCallback(
    (requisitionListId, items) => {
      setAddToRequisitionListArgs({
        requisitionListId,
        items,
      });
      setIsConfigurationPickerModalOpen(true);
    },
    []
  );

  if (!showOptionsModalIfNotFullyConfigured || areAllOptionsSet) {
    return <BaseComponent {...props} productConfigurationModal={null} />;
  }

  if (showOptionsModalIfNotFullyConfigured && items.length > 1) {
    if (isDev) {
      console.warn(
        "enabling configuring of products when adding to requisition list is only available for single products"
      );
    }
    return (
      <BaseComponent
        {...props}
        showOptionsModalIfNotFullyConfigured={false}
        productConfigurationModal={null}
      />
    );
  }

  const { product, selectedConfigurableOptions } = items[0];

  const productConfigurationModal = (
    <ProductConfigurationModal
      product={product}
      selectedConfigurableOptions={selectedConfigurableOptions}
      isOpen={isConfigurationPickerModalOpen}
      onRequestClose={() => setIsConfigurationPickerModalOpen(false)}
      onConfiurationsSelected={(selectedConfigurableOptions) => {
        setIsConfigurationPickerModalOpen(false);
        return addToRequisitionList(
          addToRequisitionListArgs.requisitionListId,
          [
            {
              ...(addToRequisitionListArgs.items ?? items)[0],
              selectedConfigurableOptions,
              selectedBundleOptions: {},
            },
          ]
        );
      }}
      buttonLabel={
        <FormattedMessage
          id="modules.RequisitionList.AddToRequisitionList.selectOptions"
          defaultMessage="Select options"
        />
      }
    />
  );

  return (
    <BaseComponent
      {...props}
      productConfigurationModal={productConfigurationModal}
      addToRequisitionList={addToRequisitionListOverride}
    />
  );
};

export default withProductConfigurationModal;
